// client/src/pages/OrderDetail.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Package, AlertCircle,
  CheckCircle, Clock, XCircle, X,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { getErrorMessage } from '@/utils/errorUtils';
import storeService, {
  OrderDetail as OrderDetailType,
  ActivationTimelineEvent,
} from '@/api/services/storeService';

// ── Cancel confirmation modal ─────────────────────────────────────────────────
interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isProcessing: boolean;
}

function CancelModal({ isOpen, onClose, onConfirm, isProcessing }: CancelModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t('operator.commerce.orderDetail.cancelModal.title', { defaultValue: 'Cancel Order' })}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('operator.commerce.orderDetail.cancelModal.body', {
              defaultValue: 'Are you sure you want to cancel this order? This action cannot be undone.',
            })}
          </p>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('operator.commerce.orderDetail.cancelModal.reasonLabel', { defaultValue: 'Reason (optional)' })}
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t('operator.commerce.orderDetail.cancelModal.reasonPlaceholder', {
              defaultValue: 'e.g. Changed my mind',
            })}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-[#1a1b1e] text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
          />
        </div>
        <div className="flex gap-3 p-6 pt-0">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {t('operator.commerce.common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={isProcessing}
            className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('operator.commerce.orderDetail.cancelModal.confirm', { defaultValue: 'Yes, Cancel Order' })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status helpers ────────────────────────────────────────────────────────────
function StatusIcon({ status }: { status: string }) {
  switch (status.toLowerCase()) {
    case 'completed':  return <CheckCircle className="w-6 h-6 text-green-600" />;
    case 'processing': return <Clock className="w-6 h-6 text-blue-600" />;
    case 'pending':    return <Clock className="w-6 h-6 text-yellow-600" />;
    case 'cancelled':  return <XCircle className="w-6 h-6 text-red-600" />;
    default:           return <Package className="w-6 h-6 text-gray-600" />;
  }
}

function statusStyle(status: string) {
  switch (status.toLowerCase()) {
    case 'completed':  return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'processing': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'pending':    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'cancelled':  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    default:           return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
  }
}

// ── Main component ────────────────────────────────────────────────────────────
const OrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [order, setOrder]               = useState<OrderDetailType | null>(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling]     = useState(false);
  const [cancelMessage, setCancelMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await storeService.getOrderById(id);
      setOrder(data);
    } catch (err: unknown) {
      const axiosErr = err instanceof AxiosError
        ? err as AxiosError<{ error?: string }>
        : null;
      const status = axiosErr?.response?.status;
      setError(
        status === 404
          ? t('operator.commerce.orderDetail.errors.notFound', { defaultValue: 'Order not found' })
          : axiosErr?.response?.data?.error ?? getErrorMessage(err) ?? t('operator.commerce.orderDetail.errors.generic', {
              defaultValue: 'Failed to load order. Please try again.',
            })
      );
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const handleCancelConfirm = async (reason: string) => {
    if (!order) return;
    setCancelling(true);
    try {
      await storeService.cancelOrder(order.id, reason);
      setShowCancelModal(false);
      setCancelMessage({
        type: 'success',
        text: t('operator.commerce.orderDetail.cancelSuccess', {
          defaultValue: 'Order cancelled successfully. Refund initiated.',
        }),
      });
      // Refresh order to show updated status
      fetchOrder();
    } catch (err: unknown) {
      setShowCancelModal(false);
      setCancelMessage({
        type: 'error',
        text: getErrorMessage(err) ?? t('operator.commerce.orderDetail.cancelError', {
          defaultValue: 'Failed to cancel order. Please try again.',
        }),
      });
    } finally {
      setCancelling(false);
      setTimeout(() => setCancelMessage(null), 6000);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // ── Error / not found ─────────────────────────────────────────────────────
  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <p className="text-red-500 dark:text-red-400 mb-4">
            {error ?? t('operator.commerce.orderDetail.errors.notFound', { defaultValue: 'Order not found' })}
          </p>
          <button
            onClick={() => navigate('/operator/orders')}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
          >
            {t('operator.commerce.orderDetail.backToOrders', { defaultValue: 'Back to Orders' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Order #{order.orderNumber} | KonectX</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* Cancel result toast */}
          {cancelMessage && (
            <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 border ${
              cancelMessage.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
            }`}>
              {cancelMessage.type === 'success'
                ? <CheckCircle className="w-5 h-5 flex-shrink-0" />
                : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
              <span className="text-sm font-medium">{cancelMessage.text}</span>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/operator/orders')}
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-5"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('operator.commerce.orderDetail.backToOrders', { defaultValue: 'Back to Orders' })}
            </button>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-1">
                  {t('operator.commerce.orders.orderHash', { defaultValue: 'Order #{{num}}', num: order.orderNumber })}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('operator.commerce.orders.placedOn', { defaultValue: 'Placed on {{date}}', date: order.placedAtFormatted })}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <StatusIcon status={order.status} />
                <span className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize ${statusStyle(order.status)}`}>
                  {t(`operator.commerce.orders.filter.${order.status.toLowerCase()}`, { defaultValue: order.status })}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* ── Left: items + timeline ── */}
            <div className="lg:col-span-2 space-y-6">

              {/* Order items */}
              <div className="bg-white dark:bg-[#333436] rounded-2xl p-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">
                  {t('operator.commerce.checkout.orderItems', { defaultValue: 'Order Items' })}
                </h2>
                <div className="space-y-4">
                  {order.items.map((item, i) => (
                    <div
                      key={i}
                      className="flex gap-4 items-center pb-4 border-b border-gray-100 dark:border-gray-700 last:border-0 last:pb-0"
                    >
                      <div className="w-20 h-20 bg-gray-50 dark:bg-[#1a1b1e] rounded-xl flex items-center justify-center flex-shrink-0">
                        {item.thumbnailUrl ? (
                          <img
                            src={item.thumbnailUrl}
                            alt={item.name}
                            className="w-full h-full object-contain p-2"
                            onError={e => {
                              (e.target as HTMLImageElement).src =
                                'https://placehold.co/80x80/1a1a2e/60a5fa?text=KX';
                            }}
                          />
                        ) : (
                          <Package className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 dark:text-white truncate">{item.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          {t('operator.commerce.common.quantity', { defaultValue: 'Quantity' })}: {item.quantity}
                          {' · '}
                          ${item.unitPrice.toFixed(2)} {t('operator.commerce.common.each', { defaultValue: 'each' })}
                        </p>
                      </div>
                      {/* lineTotal from server — no client multiplication */}
                      <p className="text-lg font-bold text-gray-800 dark:text-white flex-shrink-0">
                        ${item.lineTotal.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Activation Timeline — rendered from API activationTimeline array */}
              <div className="bg-white dark:bg-[#333436] rounded-2xl p-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">
                  {t('operator.commerce.orderDetail.timeline', { defaultValue: 'Activation Timeline' })}
                </h2>
                <div className="space-y-4">
                  {order.activationTimeline.map((event: ActivationTimelineEvent, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        event.completed
                          ? 'bg-blue-100 dark:bg-blue-900/30'
                          : 'bg-gray-100 dark:bg-gray-800'
                      }`}>
                        {event.completed
                          ? <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          : <Clock className="w-5 h-5 text-gray-400" />}
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold ${
                          event.completed
                            ? 'text-gray-800 dark:text-white'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {event.label === 'Order Placed' ? t('operator.commerce.orderDetail.orderPlaced', { defaultValue: 'Order Placed' }) :
                           event.label === 'Ready to Earn' ? t('operator.commerce.orderDetail.readyToEarn', { defaultValue: 'Ready to Earn' }) :
                           event.label}
                        </h3>
                        {event.completed && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            {event.timestampFormatted}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right: order summary ── */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-[#333436] rounded-2xl p-6 sticky top-4">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">
                  {t('operator.commerce.common.orderSummary', { defaultValue: 'Order Summary' })}
                </h2>

                {/* Server-computed summary */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-500 dark:text-gray-400 text-sm">
                    <span>{t('operator.commerce.common.subtotal', { defaultValue: 'Subtotal' })}</span>
                    <span className="font-semibold">${order.summary.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-500 dark:text-gray-400 text-sm">
                    <span>{t('operator.commerce.common.platformFee', { defaultValue: 'Platform Fee' })}</span>
                    <span className="font-semibold">${order.summary.platformFee.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                    <div className="flex justify-between text-xl font-bold text-gray-800 dark:text-white">
                      <span>{t('operator.commerce.common.total', { defaultValue: 'Total' })}</span>
                      <span>${order.summary.total.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{order.summary.currency}</p>
                  </div>
                </div>

                {/* Order metadata */}
                <div className="border-t border-gray-100 dark:border-gray-700 pt-5 space-y-3">
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                      {t('operator.commerce.orderDetail.orderId', { defaultValue: 'Order ID' })}
                    </p>
                    <p className="font-mono text-sm font-semibold text-gray-800 dark:text-white break-all">{order.id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                      {t('operator.commerce.orderDetail.orderDate', { defaultValue: 'Order Date' })}
                    </p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">{order.placedAtFormatted}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                      {t('operator.commerce.orderDetail.paymentMethod', { defaultValue: 'Payment Method' })}
                    </p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white capitalize">{t(`operator.commerce.checkout.payment.${order.paymentMethod}`, { defaultValue: order.paymentMethod })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                      {t('operator.commerce.orderDetail.paymentStatus', { defaultValue: 'Payment Status' })}
                    </p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white capitalize">{t(`operator.commerce.orderDetail.status.${order.paymentStatus}`, { defaultValue: order.paymentStatus })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                      {t('operator.commerce.orderDetail.totalItems', { defaultValue: 'Total Items' })}
                    </p>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">
                      {t('operator.commerce.common.itemCount', { defaultValue: '{{count}} items', count: order.summary.itemCount })}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 space-y-2">
                  <button
                    onClick={() => navigate('/operator/store')}
                    className="w-full py-3 bg-gray-800 dark:bg-blue-700 text-white rounded-xl font-semibold hover:bg-gray-900 dark:hover:bg-blue-600 transition-colors"
                  >
                    {t('operator.commerce.common.continueShopping', { defaultValue: 'Continue Shopping' })}
                  </button>

                  {/* Cancel button — only for pending orders */}
                  {order.status === 'pending' && (
                    <button
                      onClick={() => setShowCancelModal(true)}
                      className="w-full py-3 bg-white dark:bg-[#1a1b1e] border border-red-500 text-red-500 rounded-xl font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      {t('operator.commerce.orderDetail.cancelOrder', { defaultValue: 'Cancel Order' })}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CancelModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        isProcessing={cancelling}
      />
    </>
  );
};

export default OrderDetail;